import secureApi from "../../utils/secureApi";
import publicApi from "../../lib/api";
import { Sport, SportPayload } from "./types";

const ROOT = "/api/v1/sports";

const defaultVisibility = {
  showOnWebsiteLeadsForm: false,
  showOnAppFilters: false,
  showOnWebsite: false,
};

export const normalizeSport = (source: Partial<Sport>): Sport => ({
  _id: source._id || "",
  name: source.name || "",
  shortName: source.shortName || source.name || "",
  code: source.code || "",
  slug: source.slug || "",
  isTeamSport: Boolean(source.isTeamSport),
  isActive: source.isActive !== false,
  iconUrl: source.iconUrl || "",
  modules: source.modules || {},
  visibility: { ...defaultVisibility, ...(source.visibility || {}) },
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

const unwrapList = (response: any): Sport[] => {
  const data = response?.data ?? response;
  const items = Array.isArray(data) ? data : data?.paginatedResults ?? data?.results ?? [];
  return Array.isArray(items) ? items.map(normalizeSport) : [];
};

const unwrapItem = (response: any): Sport =>
  normalizeSport(response?.data ?? response);

export const getTournamentEnabledSports = (sports: Sport[]): Sport[] =>
  sports
    .filter((sport) => sport.isActive && sport.modules?.tournament?.enabled)
    .sort((left, right) => left.name.localeCompare(right.name));

export const mergeSportUpdate = (
  current: Sport,
  patch: Partial<SportPayload>
): Partial<SportPayload> => ({
  ...patch,
  modules: {
    ...current.modules,
    ...(patch.modules || {}),
    tournament: {
      ...(current.modules?.tournament || { enabled: false }),
      ...(patch.modules?.tournament || {}),
    },
  },
  visibility: {
    ...current.visibility,
    ...(patch.visibility || {}),
  },
});

export async function listSports(): Promise<Sport[]> {
  const response = await publicApi.get(ROOT);
  return unwrapList(response.data);
}

export async function getSport(id: string): Promise<Sport> {
  return unwrapItem(await secureApi.get(`${ROOT}/${id}`));
}

export async function createSport(payload: SportPayload): Promise<Sport> {
  return unwrapItem(await secureApi.post(ROOT, payload));
}

export async function updateSport(
  id: string,
  current: Sport,
  patch: Partial<SportPayload>
): Promise<Sport> {
  return unwrapItem(await secureApi.put(`${ROOT}/${id}`, mergeSportUpdate(current, patch)));
}

export async function deleteSport(id: string): Promise<void> {
  await secureApi.delete(`${ROOT}/${id}`);
}
